import Cookies from "js-cookie";
import { delay, groupBy, throttle, uniq } from "lodash";
import moment from "moment-timezone";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../api";
import {
  CHATTING,
  CHUNK_SIZE,
  conversationType,
  eventBusType,
  MAX_CHUNK_LENGTH,
  MESSAGE_STATUS,
  role,
} from "../config/Constant";
import { useInfoUser } from "../store/UserStore";
import {
  extractUserIds,
  isAudioFile,
  isVideoFile,
  openNotificationError,
  openNotificationSuccess,
  processFileUrl,
  sanitizeMentions,
  sortMessageListDesc,
} from "../utils/Utils";
import { useMenuContext } from "./MenuContext";

import { chatEvents$, sendChat, socketStatus$ } from "../api/webSocketRx";
import { updateOrgList } from "../utils/OrganizationHelper";
import { compressImage } from "../utils/SendFile";
import { useAuthLayoutContext } from "./AuthLayoutContext";
import { useSendingContext } from "./global/SendingProvider";
import { uploadEvent$ } from "../api/eventBus";

const REACT_APP_TYPING_TIME = process.env.REACT_APP_TYPING_TIME || 4000;
const MAX_DIMENSION = 500;

const ChatContext = createContext(null);
export const useChatContext = () => {
  return useContext(ChatContext) || {};
};

export const ChatProvider = ({ children }) => {
  /** Context */
  const {
    conversationList,
    handleUpdateConversationList,
    selectedConversation,
    setSelectedConversation,
    activeKey,
    setActiveKey,
    isMessageSearch,
    messageSearch,
    setIsMessageSearch,
    setMessageSearch,
    setIsMenu,
    setIsModalLabelGroup,
    mediaStorageType,
    setMediaStorageType,
    appendToMediaStorage,
    appendToGroupInfoResources,
    removeMediaFromStorage,
  } = useMenuContext();

  const { setOrgList: setOriginalOrgList, orgList: originalOrgList } =
    useAuthLayoutContext();
  const { selectedPersonalId } = useParams();
  const [searchParams] = useSearchParams();
  const jumpMessageId = searchParams.get("jumpMessageId");
  const timeoutRefs = useRef({});
  // use to handle scroll
  const messageRef = useRef(null);
  // use to map element uuid
  const messageMapRef = useRef({});

  const { languageMap, user } = useInfoUser();
  const navigate = useNavigate();

  const conversationRef = useRef(null);

  const {
    fakeMessages,
    pushMessage,
    pushMessages,

    pushFiles,
    completeMessage,
    completeFile,
    uploadFileV2,
    createTextMessage,
  } = useSendingContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isJumping, setIsJumping] = useState(false);

  const [searchCacheMention, setSearchCacheMention] = useState(new Map());
  const [isExitGroup, setIsExitGroup] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showAllPin, setShowAllPin] = useState(false);
  const [firstMessageUnread, setFirstMessageUnread] = useState(null);
  const [unreadInDOM, setUnreadInDOM] = useState(0);
  const [isMessageUnread, setIsMessageUnread] = useState(false);
  const [friend, setFriend] = useState(null);
  const [countReconnectedSuccessWs, setCountReconnectedSuccessWs] = useState(1);

  const [messageList, setMessageList] = useState([]);

  const [formData, setFormData] = useState({
    text: "",
    files: [],
    mediaFiles: [],
    encodePhotos: [],
    previewLink: {
      image: null,
      title: null,
      description: null,
      url: null,
    },
  });
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastMessageTs, setLastMessageTs] = useState(moment());
  const [isMoreMessage, setIsMoreMessage] = useState(false);
  const [isModalAddMember, setIsModalAddMember] = useState(false);
  const [isOpenModalConfirm, setIsOpenModalConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false);
  const [isViewTargetMessage, setIsViewTargetMessage] = useState(false);
  const [isScrollToLastMessage, setIsScrollToLastMessage] = useState(true);
  const [viewTargetMessage, setViewTargetMessage] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [scheduleMessage, setScheduleMessage] = useState(null);
  const [isChatScheduleModal, setIsChatScheduleModal] = useState(false);
  const jumpedRef = useRef(false);
  const updateInitScrollRef = useRef(false);
  const updateFirstScrollRef = useRef(false);
  const abortControllerRef = useRef(new AbortController());
  const observerRef = useRef(null);
  const inputSendRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const previousScrollRef = useRef(0);
  const wasAtBottomMessageRef = useRef(true);
  const [isEnableGroup, setIsEnableGroup] = useState(false);

  /** view mode is PANEL or MEDIA_STORAGE */
  const [viewMode, setViewMode] = useState("PANEL");
  const [resources, setResources] = useState({
    images: [],
    files: [],
    audios: [],
    videos: [],
    linkPreviews: [],
  });

  const conversationListRef = useRef(conversationList);
  const mediaStorageTypeRef = useRef(mediaStorageType);
  useEffect(() => {
    mediaStorageTypeRef.current = mediaStorageType;
  }, [mediaStorageType]);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const getMember = (memberId) => {
    const member = originalOrgList?.find((org) => org?.userCode === memberId);

    return {
      ...member,
      userId: memberId,
    };
  };

  const onBackToConversations = () => {
    setSelectedConversation(null);
    navigate("/conversation");
  };

  const fakeMessageFileBeforeUpload = (file, currentTime) => {
    return {
      totalVolume: file?.size,
      conversationId: selectedConversation?.conversationId,
      requestUuid: file?.uid,
      memberCount: 0,
      messageId: uuidv4(),
      notificationId: null,
      unreadCount: 0,
      totalChunk: Math.round(file?.size / CHUNK_SIZE),
      senderId: user?.userId,
      senderName: user?.name,
      avatar: user?.avatar,
      files: [
        {
          volume: CHUNK_SIZE,
          originalName: file?.name,
        },
      ],
      shortName: file?.name,
      contentType: "CHUNK",
      username: user?.name,
      isSelf: true,
      progress: 1,
      totalChunkUploaded: 0,
      isSending: true,
      createdAt: currentTime,
      updatedAt: currentTime,
    };
  };

  const clearFormData = () => {
    setFormData({ text: "", files: [], mediaFiles: [], encodePhotos: [] });
    setQuotation(null);
  };

  const isImageFile = (file) => {
    return file && file.type && file.type.startsWith("image/");
  };

  const sendFormData = async (formData) => {
    setIsSending(true);
    clearFormData();

    if (isViewTargetMessage) {
      await comeToLastMessage();
    }

    const allFiles = formData.files || [];
    const imageFiles = allFiles.filter((file) => isImageFile(file));

    await handleSendText(formData, imageFiles);
    await handleSendImage(imageFiles, formData);
    await handleSendFile();
    setIsSending(false);

    comeToLastMessageOnView();
  };

  /*
   * 1. push that one into fake files with status: uploading
   * 2. push to fake message for each file with status: sending
   * 3. push waiting messages to messageList
   * 4. upload one by one file with requestUuid
   * 5. with each file -> upload file chunk step by step
   * 6. with each chunk check if not exist message requestUuid -> that file is canceled -> inversely -> update messageList
   * 7. after finishing every single file -> update file status to sent
   * 8. send message containing that file resourceId in messageRequest
   * 9. receive message from websocket -> clear fake file and message fake
   * */
  const handleSendText = async (formData, imageFiles) => {
    if (
      !formData?.text ||
      formData?.text?.trim() === "" ||
      imageFiles?.length
    ) {
      return;
    }

    let preparedMessages = [];

    for (const content of splitText(formData?.text?.trim()) || []) {
      const mentionIds = extractUserIds(content);
      const requestUuid = uuidv4();

      preparedMessages.push({
        conversationId:
          formData?.conversationId || selectedConversation?.conversationId,
        requestUuid,
        content: sanitizeMentions(content),
        contentType: "TEXT",

        // local message fields
        messageId: requestUuid,
        senderId: user?.userId,
        senderName: user?.name,
        avatar: user?.avatar,
        createdAt: moment().toISOString(),
        isSelf: true,
        status: "PENDING",

        // send payload fields
        token: Cookies.get("access_token"),
        notificationId: formData?.notificationId,
        recipientId: formData?.notificationId ? formData?.recipientId : null,
        quotationId: formData?.quotation?.messageId,
        mentionIds,
        previewLink: formData?.previewLink,
      });
    }

    setMessageList((prev) =>
      sortMessageListDesc([...prev, ...preparedMessages]),
    );
    pushMessages(selectedConversation?.conversationId, preparedMessages);

    for (const preparedMessage of preparedMessages) {
      await apiFactory.messageApi.sendMessage(preparedMessage);
    }
  };

  const handleSendFile = async () => {
    if (!formData?.mediaFiles?.length) return;

    const preparedMessages = formData?.mediaFiles?.map((file, index) => {
      const currentTime = moment().add(index, "seconds").toISOString();
      return fakeMessageFileBeforeUpload(file, currentTime);
    });

    const preparedFiles = formData?.files?.map((file) => ({
      file: file,
      abortController: new AbortController(),
    }));

    setMessageList((prev) =>
      sortMessageListDesc([...prev, ...preparedMessages]),
    );
    pushFiles(preparedFiles);
    pushMessages(selectedConversation?.conversationId, preparedMessages);

    for (const preparedFile of preparedFiles) {
      const message = preparedMessages?.find(
        (preparedMessage) =>
          preparedMessage?.requestUuid === preparedFile?.file?.uid,
      );
      await sendFile(preparedFile, message);
    }
  };

  const handleSendImage = async (imageFiles, formData) => {
    if (!imageFiles?.length) return;

    const { conversationId, text, notificationId, recipientId, quotation } =
      formData;

    const requestUuid = uuidv4();
    const mentionIds = extractUserIds(text);

    const localPhotos = imageFiles.map((file) => ({
      resourceId: uuidv4(),
      pathUrl: URL.createObjectURL(file),
      originalName: file.name,
      volume: file.size,
      isUploading: true,
    }));
    /* image message can contain text*/
    const preparedMessage = {
      conversationId: selectedConversation?.conversationId,
      requestUuid: requestUuid,
      messageId: uuidv4(),
      senderId: user?.userId,
      senderName: user?.name,
      content: text || "",
      contentType: "IMAGE",
      isSending: true,
      photos: localPhotos,
      totalChunk: 0,
      totalChunkUploaded: 0,
      isSelf: true,
      createdAt: moment().toISOString(),
      updatedAt: moment().toISOString(),
      memberCount: 0,
      unreadCount: 0,
      isMention: false,
    };

    setMessageList((prev) => sortMessageListDesc([...prev, preparedMessage]));
    pushMessage(selectedConversation?.conversationId, preparedMessage);

    /* preparedFiles contain: original image and compressed image */
    let preparedFiles = [];
    for (const imageFile of imageFiles) {
      const originalFile = {
        file: imageFile,
        messageRequestUuid: preparedMessage?.requestUuid,
        abortController: new AbortController(),
      };

      preparedFiles.push(originalFile);

      if (
        imageFile?.size > CHUNK_SIZE ||
        imageFile?.width > MAX_DIMENSION ||
        imageFile?.height > MAX_DIMENSION
      ) {
        const compressedFile = await compressImage(imageFile);
        compressedFile.uid = uuidv4();

        preparedFiles.push({
          file: compressedFile,
          messageRequestUuid: preparedMessage?.requestUuid,
          originalUid: originalFile?.file?.uid,
          abortController: new AbortController(),
        });
      }
    }

    pushFiles(preparedFiles);
    createTextMessage(preparedFiles, {
      conversationId: conversationId || selectedConversation?.conversationId,
      requestUuid: requestUuid,
      content: text,
      contentType: "IMAGE",
      notificationId: notificationId,
      recipientId: notificationId ? recipientId : null,
      quotationId: quotation?.messageId,
      mentionIds: mentionIds,
    });
  };

  const sendFile = async (fakeFileMessage, message) => {
    await uploadFileV2(
      fakeFileMessage,
      selectedConversation?.conversationId,
      "CONVERSATION",
      fakeFileMessage?.abortController?.signal,
      (uploadedCount, chunkData) => { },
      null,
      null,
      eventBusType.FILE,
      message,
    );
  };

  const splitText = (inputText, maxChunkLength = MAX_CHUNK_LENGTH) => {
    if (!inputText) return [];

    const lines = inputText.split("\n");
    const resultChunks = [];
    let currentChunk = "";

    for (const line of lines) {
      const potentialLength = currentChunk.length + line.length + 1;

      if (potentialLength <= maxChunkLength) {
        currentChunk += (currentChunk ? "\n" : "") + line;
      } else {
        if (currentChunk) resultChunks.push(currentChunk);
        let remaining = line;
        while (remaining.length > maxChunkLength) {
          resultChunks.push(remaining.substring(0, maxChunkLength));
          remaining = remaining.substring(maxChunkLength);
        }
        currentChunk = remaining;
      }
    }

    if (currentChunk) resultChunks.push(currentChunk);

    return resultChunks;
  };
  const shareMessages = (messageDetail, conversationIds) => {
    try {
      const { requestUuid } = messageDetail;

      const payload = {
        conversationIds: conversationIds,
        requestUuid: requestUuid,
      };

      apiFactory.messageApi.shareMessageByConversationId(payload);
    } catch (error) {
      console.error("Error share messages:", error);
    }
  };

  const removeMessage = (requestUuid) => {
    try {
      const payload = {
        token: Cookies.get("access_token"),
        conversationId: selectedConversation?.conversationId,
        requestUuid,
        contentType: MESSAGE_STATUS.REMOVE_MESSAGE,
      };

      apiFactory.messageApi.removeMessage(payload);

      updateMessageRemove(payload);
    } catch (error) {
      console.error("Error remove message:", error);
    }
  };

  const updateDisableGroup = (message) => {
    if (message?.conversationId !== selectedConversation?.conversationId)
      return;

    selectedConversation.conversationStatus = "INACTIVE";
    setSelectedConversation({ ...selectedConversation });
  };

  const updateEnableGroup = (message) => {
    if (message?.conversationId !== selectedConversation?.conversationId)
      return;

    selectedConversation.conversationStatus = "ACTIVE";
    setSelectedConversation({ ...selectedConversation });
  };

  const updateTyping = (message) => {
    if (message?.conversationId !== selectedConversation?.conversationId)
      return;

    setTypingUsers((prev) => ({
      ...prev,
      [message?.userId]: {
        userName: message?.message,
        typing: true,
      },
    }));

    if (timeoutRefs.current[message?.userId]) {
      clearTimeout(timeoutRefs.current[message?.userId]);
    }

    timeoutRefs.current[message?.userId] = setTimeout(() => {
      setTypingUsers((prev) => {
        const newState = { ...prev };
        delete newState[message?.userId];
        return newState;
      });
    }, 2500);
  };

  const updateMessageListAfterReaction = (message) => {
    if (!message?.requestUuid) return;

    setMessageList((prev) => {
      const index = prev?.findIndex(
        (s) => s?.requestUuid === message?.requestUuid,
      );
      if (index === -1) return prev;

      const next = [...prev];
      next[index] = getUpdatedMessageReaction(prev[index], message);
      return sortMessageListDesc(next);
    });
  };

  const updatePinnedMessage = (basicConversationInfo, pinnedMessageList) => {
    if (
      basicConversationInfo?.conversationId ===
      selectedConversation?.conversationId
    ) {
      setPinnedMessages(pinnedMessageList);
    }

    const conversationListCurrent = conversationListRef?.current || [];

    const conversationId = basicConversationInfo?.conversationId;
    if (!conversationId) return;

    const index = conversationListCurrent?.findIndex(
      (item) => item?.conversationId === conversationId,
    );
    if (index === -1) return;

    const unreadCount =
      !selectedConversation?.conversationId ||
        selectedConversation?.conversationId === conversationId
        ? 0
        : (conversationListCurrent[index]?.unreadCount || 0) + 1;

    const updatedConversation = {
      ...conversationListCurrent[index],
      lastMessageTs: basicConversationInfo?.lastMessageTs,
      unreadCount,
      lastSenderId: basicConversationInfo?.lastSenderId,
      lastMessage: basicConversationInfo?.lastMessage,
      lastMessageType: basicConversationInfo?.lastMessageType,
    };

    const updatedConversationList = [...conversationListCurrent];
    updatedConversationList[index] = updatedConversation;

    handleUpdateConversationList(updatedConversationList);
  };

  const getUpdatedMessage = (item, oldItem) => {
    if (
      item?.senderId !== user?.userId &&
      messageRef?.current &&
      messageRef?.current?.scrollHeight > messageRef?.current?.clientHeight &&
      messageRef.current?.scrollTop + messageRef?.current?.clientHeight <
      messageRef?.current?.scrollHeight
    ) {
      setIsNewMessage(true);
    }

    const member = getMember(item?.senderId);

    return {
      ...item,
      username: member?.name,
      isSelf: item?.senderId === user?.userId,
      conversationId: selectedConversation?.conversationId,
      photos: item?.photos
        ? processFileUrl(
          item?.photos,
          "/conversation/",
          selectedConversation?.conversationId,
        )
        : item?.photoRequests?.map((s) => s?.contentCode),
      progress: oldItem?.progress !== undefined ? oldItem?.progress + 1 : 0,
      contentType: item?.contentType,
      isSending: oldItem?.progress !== item?.totalChunk - 1,
      totalChunk: item?.totalChunk,
      status: item?.senderId === user?.userId ? "RECEIVED" : item?.status,
    };
  };

  const sortByCreatedAt = (arr) =>
    [...(arr || [])].sort(
      (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
    );

  const applyMessage = (msg) => {
    if (msg?.conversationId !== selectedConversation?.conversationId) return;
    setMessageList((prev) => {
      return sortMessageListDesc(
        upsertByRequestUuid(prev, msg, getUpdatedMessage),
      );
    });
  };

  const applyMessages = (msgs = []) => {
    const shareMsg = msgs.find(
      (msg) => msg?.conversationId === selectedConversation?.conversationId,
    );
    if (!shareMsg) return;
    applyMessage(shareMsg);
  };

  const upsertByRequestUuid = (prev, nextMsg, getUpdatedMessage) => {
    if (!nextMsg?.requestUuid) {
      return prev;
    }

    if (jumpedRef.current) {
      setIsNewMessage(true);
      return prev;
    }

    const idx = prev.findIndex((m) => m?.requestUuid === nextMsg.requestUuid);

    // update
    if (idx !== -1) {
      const updated = getUpdatedMessage(nextMsg, prev[idx]);
      if (!updated) return prev;
      const copy = [...prev];
      copy[idx] = updated;

      return sortByCreatedAt(copy);
    }

    // add
    const updated = getUpdatedMessage(nextMsg);
    if (!updated) return prev;
    return sortByCreatedAt([...prev, updated]);
  };

  const getUpdatedMessageReaction = (current, item) => {
    return {
      ...current,
      ...(item?.respondent !== undefined
        ? { respondent: item.respondent }
        : {}),
      ...(item?.countReactionType !== undefined
        ? { countReactionType: item.countReactionType }
        : {}),
    };
  };

  const updateMessageRemove = (item) => {
    const removingMessage = messageList?.find(
      (s) => s?.requestUuid === item?.requestUuid,
    );

    if (removingMessage) {
      removingMessage.contentType = MESSAGE_STATUS.REMOVE_MESSAGE;
      setMessageList(sortMessageListDesc([...messageList]));
      openNotificationSuccess(
        languageMap?.["success.removeMessage"] ??
        "You removed message successfully",
      );
    }

    handleRemoveMediaFromPanel({
      requestUuid: item?.requestUuid,
      messageId: removingMessage?.messageId || item?.messageId,
      conversationId: item?.conversationId || selectedConversation?.conversationId,
    });
  };

  const refreshChatMessage = () => {
    setIsMessageSearch(false);
    setMessageSearch({
      ...messageSearch,
      content: "",
    });
    setIsMenu(false);
  };

  const onCloseChatMessage = () => {
    setIsMessageSearch(false);

    if (messageSearch?.content?.trim()?.length > 0) {
      setMessageSearch({
        ...messageSearch,
        content: "",
      });
    }

    setIsMenu(false);
  };

  const onSearch = () => {
    if (isMessageSearch) {
      onCloseChatMessage();
    } else {
      setIsMessageSearch(!isMessageSearch);
      setIsMenu(false);
    }
  };

  const openAddMemberModal = () => {
    setIsModalAddMember(true);
    setIsMenu(false);
  };

  const showModalLabel = () => {
    setIsModalLabelGroup(true);
    setIsMenu(false);
  };

  const openModalConfirm = () => {
    setIsOpenModalConfirm(true);
    setIsMenu(false);
  };

  const updateMessageListAfterSeen = (seenMessageTs) => {
    const { conversationId, lastOldSeenTs } = seenMessageTs || {};
    if (!conversationId || !lastOldSeenTs) return;
    if (conversationId !== selectedConversation?.conversationId) return;

    const lastSeenMs = new Date(lastOldSeenTs).getTime();
    if (Number.isNaN(lastSeenMs)) return;

    setMessageList((prev) =>
      (prev || []).map((msg) => {
        // ✅ giữ nguyên shadow hoặc msg không có createdAt
        if (msg?.shadow || !msg?.createdAt) return msg;

        const msgMs = new Date(msg.createdAt).getTime();
        if (Number.isNaN(msgMs)) return msg;

        // msg <= lastOldSeenTs nghĩa là đã seen
        const isSeen = msgMs <= lastSeenMs;

        // tuỳ logic bạn muốn: nếu seen thì giảm unreadCount 1 (đang làm vậy)
        if (!isSeen) return msg;

        const unreadCount = Math.max(msg?.unreadCount || 0, 0);
        if (unreadCount === 0) return msg;

        return { ...msg, unreadCount: unreadCount - 1 };
      }),
    );
  };

  const handleImagePanel = ({ message }) => {
    const formatPhotosToImages = (message) => {
      const photos = message?.photos || [];
      return (
        photos.map((photo) => {
          const imgUrl =
            photo?.thumbnailPreviewPath ||
            photo?.mainPath ||
            photo?.location ||
            photo?.pathUrl;

          return {
            ...photo,
            id: photo?.id || photo?.resourceId || null,
            mainPath: photo?.mainPath || photo?.location || imgUrl,
            thumbnailPreviewPath:
              photo?.thumbnailPreviewPath || photo?.mainPath || imgUrl,
            messageId: message?.messageId,
            requestUuid: message?.requestUuid,
            senderId: message?.senderId,
            createdAt: message?.createdAt,
            linkPreview: null,
            photos: null,
            media: photo,
          };
        }) ?? []
      );
    };

    const newImages = formatPhotosToImages(message);

    setResources((prev) => ({
      ...prev,
      images: [...newImages, ...(prev?.images || [])].slice(0, 6),
    }));

    appendToGroupInfoResources?.(newImages);

    if (mediaStorageTypeRef.current === "IMAGE") {
      appendToMediaStorage?.(newImages);
    }
  };

  const handleFilePanel = ({ message }) => {
    const formatFileMessage = (message) => {
      const file = message?.file || message?.files?.[0];
      if (!file) return null;

      return {
        ...file,
        id: file?.id || null,
        mainPath: file?.mainPath || file?.location,
        videoImgPreviewPath: file?.videoImgPreviewPath,
        fileName: file?.fileName || file?.originalName,
        originalName: file?.originalName || file?.fileName,
        volume: file?.volume || file?.fileSize,
        messageId: message?.messageId,
        requestUuid: message?.requestUuid,
        senderId: message?.senderId,
        createdAt: message?.createdAt,
        linkPreview: null,
        photos: null,
        media: file,
      };
    };

    const newFile = formatFileMessage(message);
    if (!newFile) return;

    const fileName =
      newFile?.originalName || newFile?.fileName || newFile?.mainPath || "";
    const cleanFileName = fileName.split("?")[0];

    const isVideo =
      newFile?.contentType === "VIDEO" ||
      newFile?.contentType?.toLowerCase()?.includes("video") ||
      isVideoFile(cleanFileName) ||
      Boolean(newFile?.videoImgPreviewPath);

    const isAudio =
      newFile?.contentType === "AUDIO" ||
      newFile?.contentType?.toLowerCase()?.includes("audio") ||
      isAudioFile(cleanFileName);

    let targetCategory = "files";
    let targetMediaStorageType = "FILE";

    if (isVideo) {
      targetCategory = "videos";
      targetMediaStorageType = "VIDEO";
    } else if (isAudio) {
      targetCategory = "audios";
      targetMediaStorageType = "AUDIO";
    }

    setResources((prev) => ({
      ...prev,
      [targetCategory]: [newFile, ...(prev?.[targetCategory] || [])].slice(0, 3),
    }));

    appendToGroupInfoResources?.([newFile]);

    if (mediaStorageTypeRef.current === targetMediaStorageType) {
      appendToMediaStorage?.([newFile]);
    }
  };

  const handleRemoveMediaFromPanel = useCallback(
    (item) => {
      const requestUuid = item?.requestUuid;
      const messageId = item?.messageId;
      const conversationId =
        item?.conversationId || selectedConversation?.conversationId;

      if (!requestUuid && !messageId) return;

      removeMediaFromStorage?.(requestUuid, messageId);

      if (
        conversationId &&
        conversationId === selectedConversation?.conversationId
      ) {
        apiFactory.conversationApi
          .getRepresentResource(conversationId)
          .then((result) => {
            if (result?.data) {
              setResources(result.data);
            }
          })
          .catch((err) => {
            console.error("Error reloading resources after remove message:", err);
          });
      }
    },
    [selectedConversation?.conversationId, removeMediaFromStorage],
  );

  const directPersonal = async () => {
    /** 1. check existing conversation
     *  2. find friend
     */

    if (!selectedPersonalId) return;

    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: [user?.userId, selectedPersonalId],
      conversationType: conversationType.PERSONAL,
    });

    if (result?.status === 200 && result?.data?.conversationId) {
      navigate(`/conversation/chat/${result?.data?.conversationId}`);
      return;
    }

    const member = originalOrgList?.find(
      (m) => m.objectId?.toLowerCase() === selectedPersonalId.toLowerCase(),
    );

    setFriend({
      avatar: member?.avatar,
      name: member?.name,
      online: member?.online,
      userId: member?.userCode,
      lastTimeOnline: member?.lastTimeOnline,
    });
  };

  const handleEventData = (eventData) => {
    if (!eventData) return;

    const {
      seenMessageTs,
      type,
      message,
      messages,
      basicConversationInfo,
      pinnedMessages,
      announcer,
    } = eventData;

    if (announcer) {
      updateOrgList(setOriginalOrgList, [announcer]);
    }

    switch (type) {
      case MESSAGE_STATUS.SEEN_MESSAGE:
        updateMessageListAfterSeen(seenMessageTs);
        break;

      case MESSAGE_STATUS.TYPING:
        updateTyping(message);
        break;

      case MESSAGE_STATUS.REACTION_MESSAGE:
        // reaction thường update message hiện có
        updateMessageListAfterReaction(message);
        break;

      case MESSAGE_STATUS.ERROR:
        openNotificationError(message);
        break;

      case MESSAGE_STATUS.SHARE_MESSAGE:
        applyMessages(messages);
        break;

      case MESSAGE_STATUS.PINNED_MESSAGE:
        updatePinnedMessage(basicConversationInfo, pinnedMessages);
        applyMessage(message);
        break;

      case MESSAGE_STATUS.DISABLE_GROUP:
        updateDisableGroup(message);
        applyMessage(message);
        break;

      case MESSAGE_STATUS.ENABLE_GROUP:
        updateEnableGroup(message);
        applyMessage(message);
        break;

      case MESSAGE_STATUS.SCHEDULE:
      case MESSAGE_STATUS.UPDATE_SCHEDULE:
      case MESSAGE_STATUS.DELETE_SCHEDULE: {
        // ✅ không mutate messageList.forEach nữa — update bằng setMessageList
        if (message?.conversationId !== selectedConversation?.conversationId)
          break;

        setMessageList((prev) => {
          const next = prev.map((m) => {
            if (m?.messageScheduleId !== message?.messageScheduleId) return m;

            if (
              type === MESSAGE_STATUS.SCHEDULE &&
              m?.scheduleStatus !== "DELETED"
            ) {
              return { ...m, scheduleStatus: message?.scheduleStatus };
            }
            // UPDATE/DELETE => DELETED
            return { ...m, scheduleStatus: "DELETED" };
          });
          return next;
        });

        applyMessage(message);
        break;
      }

      case MESSAGE_STATUS.IMAGE:
        if (message?.conversationId === selectedConversation?.conversationId) {
          handleImagePanel({ message });
        }
        applyMessage(message);
        break;

      case MESSAGE_STATUS.FILE:
      case MESSAGE_STATUS.VIDEO:
      case MESSAGE_STATUS.AUDIO:
        if (message?.conversationId === selectedConversation?.conversationId) {
          handleFilePanel({ message });
        }
        applyMessage(message);
        break;

      /** when change member -> clear mention list */
      case MESSAGE_STATUS.REMOVE_MESSAGE:
        setSearchCacheMention(new Map());
        if (message?.conversationId === selectedConversation?.conversationId) {
          handleRemoveMediaFromPanel(message);
        }
        applyMessage(message);
        break;

      case MESSAGE_STATUS.NEW_MEMBER:
      case MESSAGE_STATUS.EXIT_GROUP:
        setSearchCacheMention(new Map());
        applyMessage(message);
        break;
      default:
        applyMessage(message);
        break;
    }
  };

  // typing throttle
  const handleTyping = useMemo(
    () =>
      throttle(
        () => {
          if (!selectedConversation?.conversationId) return;

          const payload = {
            event: "TYPING",
            message: JSON.stringify({
              conversationId: selectedConversation.conversationId,
            }),
          };

          try {
            sendChat(JSON.stringify(payload));
          } catch (error) {
            console.error("Error sending typing event:", error);
          }
        },
        REACT_APP_TYPING_TIME,
        { leading: true, trailing: false },
      ),
    [selectedConversation?.conversationId],
  );

  const fetchPinnedMessages = async () => {
    if (!selectedConversation?.conversationId) return;

    const response = await apiFactory.messageApi.getPinnedMessage({
      conversationId: selectedConversation?.conversationId,
    });

    if (response?.status !== 200) return;

    setPinnedMessages(response?.data);
  };

  const handleClickButtonIsNewMessage = () => {
    setIsNewMessage(false);
  };

  const getListUserFromMessage = async (messageList) => {
    //get all senderIds and quotation senderIds
    const userIds = [];
    for (const message of messageList) {
      userIds?.push(message?.senderId);
      if (message?.quotation) {
        userIds?.push(message?.quotation?.senderId);
      }
    }

    if (userIds?.length === 0) return;

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: uniq(userIds),
    });

    if (userResult?.status !== 200) {
      return openNotificationError(userResult?.message);
    }

    /** update organizationUser and use it instead of userList in the future */
    updateOrgList(setOriginalOrgList, userResult?.data);

    return userResult?.data;
  };

  const jumpToExistedMessage = (targetMessage) => {
    setIsLoading(true);

    const isLastMessage =
      targetMessage?.messageId ===
      messageList?.[messageList.length - 1]?.messageId;

    if (isLastMessage) {
      setIsLoading(false);
      setViewTargetMessage(targetMessage);
      handleClickButtonIsNewMessage();
      setIsJumping(false);
      return;
    }

    messageMapRef.current[targetMessage?.requestUuid]?.scrollIntoView({
      behavior: "instant",
      block: "center",
      inline: "nearest",
    });

    previousScrollRef.current = messageRef.current.scrollHeight;

    setIsLoading(false);
    setViewTargetMessage(targetMessage);
    setIsScrollToLastMessage(false);
  };

  const jumpToNotExistedMessage = async (targetMessage) => {
    setIsLoading(true);
    const res = await apiFactory.messageApi.getTargetMessage({
      limit: CHATTING.MESSAGE_LIMIT,
      minTs: targetMessage?.createdAt,
      search: { conversationId: selectedConversation?.conversationId },
      direction: "ASCE",
    });

    if (res?.status !== 200) {
      openNotificationError(res?.message);
      return;
    }

    const messList = res?.data?.map((item) => ({
      ...item,
      isSelf: item?.senderId === user?.userId,
      username: getMember(item?.senderId)?.name,
      conversationId: selectedConversation?.conversationId,
      photos: processFileUrl(
        item?.photos,
        "/conversation/",
        selectedConversation?.conversationId,
      ),
      progress: 1,
    }));

    const isLastMessage =
      targetMessage?.messageId === messList?.[messList?.length - 1]?.messageId;

    if (isLastMessage) {
      setIsLoading(false);
      setIsScrollToLastMessage(false);
      setViewTargetMessage(targetMessage);
      handleClickButtonIsNewMessage();
      setIsJumping(false);
      return;
    }

    setIsScrollToLastMessage(true);
    setMessageList([{ shadow: true }, ...messList]);
    setLastMessageTs(messList[0]?.createdAt);
    setIsMoreMessage(true);
    setIsViewTargetMessage(true);
    jumpedRef.current = true;
    delay(() => {
      messageMapRef?.current[targetMessage?.requestUuid]?.scrollIntoView({
        behavior: "instant",
        block: "center",
        inline: "nearest",
      });

      previousScrollRef.current = messageRef.current.scrollHeight;
      setIsViewTargetMessage(true);
      setIsLoading(false);
      setViewTargetMessage(targetMessage);
      setIsScrollToLastMessage(false);
    }, 500);
  };

  const jumpToMessage = useCallback(
    async (targetMessage) => {
      if (!targetMessage) return;
      setIsJumping(true);
      const existedTargetMessageIndex = messageList?.findIndex(
        (message) => message?.messageId === targetMessage?.messageId,
      );

      if (existedTargetMessageIndex > -1) {
        jumpToExistedMessage(targetMessage);
      } else {
        jumpToNotExistedMessage(targetMessage);
      }
      setIsJumping(false);
    },
    [
      messageList,
      selectedConversation?.conversationId,
      isViewTargetMessage,
      isJumping,
    ],
  );

  const retrieveMessage = async (messageId) => {
    try {
      const res = await apiFactory.messageApi.getMessageById({
        conversationId: selectedConversation?.conversationId,
        messageId,
      });

      if (res?.status !== 200) {
        openNotificationError(res?.message);
        return;
      }

      jumpToNotExistedMessage(res?.data);
    } catch (error) { }
  };

  const initMessageList = async () => {
    if (!selectedConversation?.conversationId) return;

    setIsLoading(true);
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiFactory.messageApi.getList(
        {
          limit: CHATTING.MESSAGE_LIMIT,
          maxTs: null,
          search: {
            conversationId: selectedConversation?.conversationId,
          },
        },
        { signal: abortControllerRef.current.signal },
      );

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      if (isViewTargetMessage) {
        setIsNewMessage(true);
      }

      let messages = result?.data
        ?.map((item) => ({
          ...item,
          isSelf: item?.senderId === user?.userId,
          username: getMember(item?.senderId)?.name,
          conversationId: selectedConversation?.conversationId,
          photos: processFileUrl(
            item?.photos,
            "/conversation/",
            selectedConversation?.conversationId,
          ),
          progress: 1,
        }))
        .reverse();

      messages = [
        ...messages,
        ...(fakeMessages?.[selectedConversation?.conversationId] || []),
      ];

      if (observerRef?.current) {
        observerRef?.current?.disconnect();
      }

      const sortedMessages = [...(messages || [])].sort(
        (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
      );

      setMessageList([{ shadow: true }, ...sortedMessages]);

      setLastMessageTs(messages?.[0]?.createdAt);
      setIsMoreMessage(messages?.length === CHATTING.MESSAGE_LIMIT);
      updateInitScrollRef.current = true;
      setIsLoading(false);
    } catch (error) {
      if (error.name !== "AbortError") {
        return new Error();
      }
    }
  };

  const comeToLastMessage = async () => {
    if (!selectedConversation?.conversationId) return;

    setIsLoading(true);
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiFactory.messageApi.getList(
        {
          limit: CHATTING.MESSAGE_LIMIT,
          maxTs: null,
          search: {
            conversationId: selectedConversation?.conversationId,
          },
        },
        { signal: abortControllerRef.current.signal },
      );

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      setIsNewMessage(false);

      let messages = result?.data
        ?.map((item) => ({
          ...item,
          isSelf: item?.senderId === user?.userId,
          username: getMember(item?.senderId)?.name,
          conversationId: selectedConversation?.conversationId,
          photos: processFileUrl(
            item?.photos,
            "/conversation/",
            selectedConversation?.conversationId,
          ),
          progress: 1,
        }))
        .reverse();

      messages = [
        ...messages,
        ...(fakeMessages?.[selectedConversation?.conversationId] || []),
      ];

      if (observerRef?.current) {
        observerRef?.current?.disconnect();
      }

      const sortedMessages = [...(messages || [])].sort(
        (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
      );

      setMessageList([{ shadow: true }, ...sortedMessages]);

      setLastMessageTs(messages?.[0]?.createdAt);
      setIsMoreMessage(messages?.length === CHATTING.MESSAGE_LIMIT);
      updateInitScrollRef.current = true;
      setIsLoading(false);
      setIsViewTargetMessage(false);
    } catch (error) {
      if (error.name !== "AbortError") {
        return new Error();
      }
    }
  };

  const comeToLastMessageOnView = () => {
    if (messageRef?.current?.scrollHeight <= messageRef?.current?.clientHeight)
      return;

    setIsNewMessage(false);
    delay(() => {
      if (messageRef?.current)
        messageRef.current.scrollTop = messageRef.current.scrollHeight;
    }, 200);
  };

  const scrollToBottom = async () => {
    if (isViewTargetMessage) {
      await comeToLastMessage();
      return;
    }

    if (messageRef?.current?.scrollHeight <= messageRef?.current?.clientHeight)
      return;

    setIsNewMessage(false);
    delay(() => {
      if (messageRef?.current)
        messageRef.current.scrollTop = messageRef.current.scrollHeight;
    }, 200);
  };

  const cancelUpload = (conversationId, requestUuid) => {
    completeFile(requestUuid);

    setMessageList((prev) => {
      return prev?.filter((message) => message?.requestUuid !== requestUuid);
    });
  };

  const getFirstMessageUnread = async () => {
    if (
      selectedConversation?.unreadCount <= 0 ||
      !selectedConversation?.lastSeenTs
    )
      return;

    const request = {
      conversationId: selectedConversation.conversationId,
      lastSeenTs: selectedConversation?.lastSeenTs,
    };
    const response = await apiFactory.messageApi.getFirstMessageUnread(request);

    if (response?.status !== 200) return;

    setFirstMessageUnread(response?.data);
    setIsMessageUnread(true);
  };

  useEffect(() => {
    conversationListRef.current = conversationList;
  }, [conversationList]);

  useEffect(() => {
    getFirstMessageUnread();
  }, [selectedConversation]);

  useEffect(() => {
    const sub = socketStatus$.subscribe((eventData) => {
      if (
        !eventData ||
        eventData?.status !== "CONNECTED" ||
        countReconnectedSuccessWs >= eventData?.connectedSuccess
      )
        return;

      setCountReconnectedSuccessWs(eventData?.connectedSuccess);
      initMessageList();
      fetchPinnedMessages();
    });

    return () => sub.unsubscribe();
  }, [
    selectedConversation?.conversationId,
    user?.userId,
    fakeMessages?.[selectedConversation?.conversationId]?.length,
  ]);

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData) return;

      const message = eventData?.message;

      if (
        message?.senderId === user?.userId &&
        message?.conversationId &&
        message?.requestUuid
      ) {
        completeMessage(message?.conversationId, message?.requestUuid);
      }

      // Nếu chưa chọn conversation thì bỏ qua xử lý trong ChatContext
      if (!selectedConversation?.conversationId) return;

      // Nếu muốn ChatContext chỉ xử lý event thuộc conversation đang chọn:
      const msgConversationId = eventData?.message?.conversationId;
      if (
        msgConversationId &&
        msgConversationId !== selectedConversation?.conversationId &&
        // cho phép một số event không có message.conversationId vẫn đi qua
        eventData?.type !== MESSAGE_STATUS.MARK_READ
      ) {
        return;
      }

      handleEventData(eventData);
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const sub = uploadEvent$.subscribe((eventData) => {
      if (!eventData) return;

      if (eventData?.type === "FILE") {
        const payload = eventData?.payload;
        if (payload?.conversationId !== selectedConversation?.conversationId)
          return;

        setMessageList((prev) => {
          const messageIndex = prev?.findIndex(
            (m) => m?.requestUuid === payload?.requestUuid,
          );

          if (prev?.[messageIndex]) {
            prev[messageIndex].percentProgress = payload?.percentProgress ?? 0;
          }

          return [...prev];
        });
      } else {
        // IMAGE
      }
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    setFormData({ text: "", files: [], mediaFiles: [], encodePhotos: [] });
  }, [selectedConversation?.conversationId, messageSearch]);






  useEffect(() => {
    if (selectedConversation) {
      setViewMode("PANEL");
      setIsNewMessage(false);
      setTypingUsers({});
      timeoutRefs.current = {};
      fetchPinnedMessages();
      jumpedRef.current = false;
    }
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    setSearchCacheMention(new Map());

    if (jumpMessageId) {
      retrieveMessage(jumpMessageId);
    } else {
      initMessageList(true);
    }

    inputSendRef?.current?.focus();
    setIsExitGroup(selectedConversation?.role !== role?.HOST);
    setInputValue("");
    previousScrollRef.current = 0;
    updateInitScrollRef.current = false;
    updateFirstScrollRef.current = false;
    setViewTargetMessage(null);
    setShowAllPin(false);
    setFirstMessageUnread(null);
    setIsMessageUnread(false);
    setUnreadInDOM(0);
    setIsScrollToLastMessage(false);
  }, [selectedConversation?.conversationId, jumpMessageId]);

  useEffect(() => {
    directPersonal();
  }, [selectedPersonalId, originalOrgList]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const values = useMemo(
    () => ({
      isModalAddMember,
      isOpenModalConfirm,
      loading,
      isMoreMessage,
      isNewMessage,
      conversationList,
      messageList,
      selectedConversation,
      quotation,
      formData,
      lastMessageTs,
      isViewTargetMessage,
      viewTargetMessage,
      sendFormData,
      setFormData,
      setMessageList,
      handleUpdateConversationList,
      setSelectedConversation,
      setQuotation,
      setLoading,
      setLastMessageTs,
      setIsMoreMessage,
      conversationRef,
      refreshChatMessage,
      setIsModalAddMember,
      setIsOpenModalConfirm,
      onSearch,
      onBackToConversations,
      isSending,
      setIsSending,
      openAddMemberModal,
      showModalLabel,
      openModalConfirm,
      setActiveKey,
      activeKey,
      setIsNewMessage,
      setIsViewTargetMessage,
      setViewTargetMessage,
      shareMessages,
      removeMessage,
      previousScrollRef,
      onCloseChatMessage,
      messageRef,
      isScrollToLastMessage,
      setIsScrollToLastMessage,
      handleTyping,
      typingUsers,
      pinnedMessages,
      setPinnedMessages,
      isMobile,
      setIsEnableGroup,
      isEnableGroup,
      scheduleMessage,
      setScheduleMessage,
      isChatScheduleModal,
      setIsChatScheduleModal,
      resources,
      setResources,
      handleRemoveMediaFromPanel,
      viewMode,
      setViewMode,
      fetchPinnedMessages,
      wasAtBottomMessageRef,
      messageMapRef,
      jumpedRef,
      updateInitScrollRef,
      updateFirstScrollRef,
      isLoading,
      setIsLoading,
      jumpToMessage,
      isJumping,
      setIsJumping,
      getListUserFromMessage,
      searchCacheMention,
      setSearchCacheMention,
      isExitGroup,
      setIsExitGroup,
      inputValue,
      setInputValue,
      showAllPin,
      setShowAllPin,
      firstMessageUnread,
      setFirstMessageUnread,
      unreadInDOM,
      setUnreadInDOM,
      isMessageUnread,
      setIsMessageUnread,
      initMessageList,
      friend,
      cancelUpload,
      comeToLastMessage,
      scrollToBottom,
      getMember,
    }),
    [
      messageList,
      formData,
      isModalAddMember,
      isOpenModalConfirm,
      lastMessageTs,
      isSending,
      isNewMessage,
      activeKey,
      quotation,
      isViewTargetMessage,
      viewTargetMessage,
      typingUsers,
      pinnedMessages,
      isMobile,
      isEnableGroup,
      scheduleMessage,
      isChatScheduleModal,
      resources,
      viewMode,
      conversationList,
      selectedConversation,
      loading,
      isMoreMessage,
      wasAtBottomMessageRef,
      messageMapRef,
      jumpedRef,
      updateInitScrollRef,
      updateFirstScrollRef,
      isScrollToLastMessage,
      isLoading,
      isJumping,
      searchCacheMention,
      isExitGroup,
      inputValue,
      showAllPin,
      firstMessageUnread,
      unreadInDOM,
      isMessageUnread,
      friend,
    ],
  );

  return <ChatContext.Provider value={values}>{children}</ChatContext.Provider>;
};
